import React, { useRef, useState, useEffect, useMemo } from "react";
import { Annotation, Point } from "../types/annotation.types";
import allImgPaths from "assets/images";

interface Props {
  imageSrc: string;
  // annotations: Annotation[];
  annotations: any[];
  onAddPoint: (point: Point) => void;
  tempPoints: { start?: Point; end?: Point };
  imageRef: any;
  canvasRef: any;
  imageScale?: number;
  fontInfo?: any;
  onUpdateAnnotation?: (index: number, ann: any, commit?: boolean) => void;
  color?: string;
}

const AnnotationCanvas: React.FC<Props> = ({
  imageSrc,
  annotations,
  onAddPoint,
  tempPoints,
  imageRef,
  canvasRef,
  imageScale = 1, // Default scale is 1 (100%)
  fontInfo,
  onUpdateAnnotation,
  color = "red"
}) => {
  const [imgDims, setImgDims] = useState({ width: 0, height: 0 });

  const [fontSize, setFontSize] = useState<any>(fontInfo?.size || 24)
  const [fontName, setFontName] = useState<any>(fontInfo?.name?.replace(/\.ttf$/, "") || 'Arial')

  // Per-annotation text bounding boxes (canvas px), recorded on each draw for drag hit-testing.
  const textRectsRef = useRef<({ x: number; y: number; w: number; h: number } | null)[]>([]);
  const suppressClickRef = useRef(false);

  console.log({ annotations })

  const handleClick = (e: any) => {
    if (suppressClickRef.current) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Normalize coordinates to 0-1000 scale (like in ImageVisualizer)
    const xNorm = Math.round((x / rect.width) * 1000);
    const yNorm = Math.round((y / rect.height) * 1000);

    onAddPoint({ x: xNorm, y: yNorm });
  };

  const toCanvasPoint = (clientX: number, clientY: number) => {
    const rect = imageRef.current.getBoundingClientRect();
    return {
      x: ((clientX - rect.left) / rect.width) * imgDims.width,
      y: ((clientY - rect.top) / rect.height) * imgDims.height,
    };
  };

  const hitTextIndex = (p: Point) => {
    const rects = textRectsRef.current;
    for (let i = rects.length - 1; i >= 0; i--) {
      const r = rects[i];
      if (r && p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h)
        return i;
    }
    return -1;
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    if (!onUpdateAnnotation || !imageRef.current || !imgDims.width) return;
    const p = toCanvasPoint(e.clientX, e.clientY);
    const index = hitTextIndex(p);
    if (index === -1) return;
    e.preventDefault();
    suppressClickRef.current = true;
    const orig = annotations[index];
    const xScale = imgDims.width / 1000;
    const yScale = imgDims.height / 1000;
    let latest = orig;
    const onMove = (ev: MouseEvent) => {
      const q = toCanvasPoint(ev.clientX, ev.clientY);
      // Text hangs off `end`, the head stays at `start`
      latest = {
        ...orig,
        end: {
          x: orig.end.x + (q.x - p.x) / xScale,
          y: orig.end.y + (q.y - p.y) / yScale,
        },
      };
      onUpdateAnnotation(index, latest);
    };
    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      onUpdateAnnotation(index, latest, true);
      setTimeout(() => (suppressClickRef.current = false), 0);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!onUpdateAnnotation || !imageRef.current || !imgDims.width) return;
    const over = hitTextIndex(toCanvasPoint(e.clientX, e.clientY)) !== -1;
    (e.currentTarget as HTMLElement).style.cursor = over ? "move" : "";
  };

  // Calculate dynamic font size based on image dimensions and scale factor
  const calculateDynamicFontSize = useMemo(() => {
    if (!imageRef.current || !imgDims.width || !imgDims.height) {
      return 22;
    }

    const avgDimension = (imgDims.width + imgDims.height) / 2;
    const baseFontSize = Math.min(
      32,
      Math.max(10, Math.round(avgDimension * 0.03))
    );

    let finalFontSize;
    if (imageScale <= 1.0) {
      finalFontSize = Math.max(10, Math.round(baseFontSize * imageScale * 1.0));
    } else {
      finalFontSize = Math.max(
        10,
        Math.round(baseFontSize / (imageScale * 1.0))
      );
    }

    return finalFontSize;
  }, [imgDims.width, imgDims.height, imageScale]);

  // Calculate dynamic arrow properties based on font size and image scale
  const calculateArrowProperties = useMemo(() => {
    const baseLineWidth = 2;
    const baseHeadLength = 10;

    let lineWidth, headLength;

    if (imageScale <= 1.0) {
      lineWidth = Math.max(1, Math.round(baseLineWidth * imageScale));
      headLength = Math.max(5, Math.round(baseHeadLength * imageScale));
    } else {
      lineWidth = Math.max(1, Math.round(baseLineWidth / imageScale));
      headLength = Math.max(5, Math.round(baseHeadLength / imageScale));
    }

    return { lineWidth, headLength };
  }, [imageScale]);

  // Original - One
  // const drawArrow = (
  //   ctx: CanvasRenderingContext2D,
  //   start: Point,
  //   end: Point,
  //   text: string
  // ) => {
  //   const { headLength, lineWidth } = calculateArrowProperties;
  //   // Reverse the direction: we want arrow to point from text toward the object
  //   // so we're drawing from end to start
  //   const dx = start.x - end.x;
  //   const dy = start.y - end.y;
  //   const angle = Math.atan2(dy, dx);

  //   // Draw the arrow line with dynamic width
  //   ctx.beginPath();
  //   ctx.lineWidth = lineWidth;
  //   ctx.moveTo(end.x, end.y);
  //   ctx.lineTo(start.x, start.y);
  //   ctx.stroke();

  //   // Draw arrow head at the start point (pointing toward the annotated item)
  //   ctx.beginPath();
  //   ctx.moveTo(start.x, start.y);
  //   ctx.lineTo(
  //     start.x - headLength * Math.cos(angle - Math.PI / 6),
  //     start.y - headLength * Math.sin(angle - Math.PI / 6)
  //   );
  //   ctx.lineTo(
  //     start.x - headLength * Math.cos(angle + Math.PI / 6),
  //     start.y - headLength * Math.sin(angle + Math.PI / 6)
  //   );
  //   ctx.lineTo(start.x, start.y);
  //   ctx.fill();

  //   if (!text) return;

  //   // Text rendering with dynamic font size

  //   ctx.font = `${calculateDynamicFontSize}px Arial`;
  //   ctx.textAlign = "left";
  //   ctx.textBaseline = "top";
  //   ctx.fillStyle = "red";

  //   // Calculate text position near the end point (where the text should be)
  //   // Use a larger offset based on arrow line length to prevent text-arrow overlap
  //   const arrowLength = Math.sqrt(
  //     Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
  //   );
  //   const textOffset = Math.max(80, arrowLength * 0.5); // Dynamic offset based on arrow length
  //   const reverseAngle = Math.atan2(end.y - start.y, end.x - start.x);
  //   const textX = end.x + textOffset * Math.cos(reverseAngle);
  //   const textY = end.y + textOffset * Math.sin(reverseAngle);

  //   // Create text box with background
  //   const maxWidth = 250; // Maximum width of the text box
  //   const lineHeight = 24; // Increased line height for larger font
  //   const padding = 10;

  //   // Wrap text into lines
  //   const words = text.split(" ");
  //   let line = "";
  //   const lines: string[] = [];

  //   for (let i = 0; i < words.length; i++) {
  //     const testLine = line + words[i] + " ";
  //     const metrics = ctx.measureText(testLine);

  //     if (metrics.width > maxWidth && i > 0) {
  //       lines.push(line);
  //       line = words[i] + " ";
  //     } else {
  //       line = testLine;
  //     }
  //   }
  //   lines.push(line);
  //   // Text dimensions calculation (keeping this for potential future use)
  //   const textWidth = lines.reduce((maxWidth, line) => {
  //     const metrics = ctx.measureText(line);
  //     return Math.max(maxWidth, metrics.width);
  //   }, 0);

  //   const textHeight = lines.length * lineHeight;
  //   // Background removed as requested

  //   // Draw text lines
  //   ctx.fillStyle = "red";
  //   lines.forEach((line, index) => {
  //     ctx.fillText(line, textX, textY + index * lineHeight);
  //   });
  // };

  // New - 1
  const drawArrow = (
    ctx: CanvasRenderingContext2D,
    start: Point,
    end: Point,
    text: string,
    index?: number
  ) => {
    const { headLength, lineWidth } = calculateArrowProperties;

    const dx = start.x - end.x;
    const dy = start.y - end.y;
    const angle = Math.atan2(dy, dx);

    ctx.beginPath();
    ctx.lineWidth = lineWidth;
    const extension = 50;
    const extendedEndX = end.x - extension * Math.cos(angle);
    const extendedEndY = end.y - extension * Math.sin(angle);

    // ctx.moveTo(end.x, end.y);
    ctx.moveTo(extendedEndX, extendedEndY);
    ctx.lineTo(start.x, start.y);
    ctx.stroke();

    ctx.beginPath();
    ctx.moveTo(start.x, start.y);
    ctx.lineTo(
      start.x - headLength * Math.cos(angle - Math.PI / 6),
      start.y - headLength * Math.sin(angle - Math.PI / 6)
    );
    ctx.lineTo(
      start.x - headLength * Math.cos(angle + Math.PI / 6),
      start.y - headLength * Math.sin(angle + Math.PI / 6)
    );
    ctx.lineTo(start.x, start.y);
    ctx.fill();

    if (!text) return;

    ctx.font = `${calculateDynamicFontSize}px Arial`;
    ctx.textAlign = "left";
    ctx.textBaseline = "top";
    ctx.fillStyle = color;

    const arrowLength = Math.sqrt(
      Math.pow(end.x - start.x, 2) + Math.pow(end.y - start.y, 2)
    );
    const textOffset = Math.max(80, arrowLength * 0.5);
    const reverseAngle = Math.atan2(end.y - start.y, end.x - start.x);
    let textX = end.x + textOffset * Math.cos(reverseAngle);
    let textY = end.y + textOffset * Math.sin(reverseAngle);

    const maxWidth = 250;
    const lineHeight = 24;
    const padding = 10;

    const words = text.split(" ");
    let line = "";
    const lines: string[] = [];

    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + " ";
      const metrics = ctx.measureText(testLine);
      if (metrics.width > maxWidth && i > 0) {
        lines.push(line);
        line = words[i] + " ";
      } else {
        line = testLine;
      }
    }
    lines.push(line);

    const textWidth = lines.reduce((max, l) => {
      return Math.max(max, ctx.measureText(l).width);
    }, 0);
    const textHeight = lines.length * lineHeight;

    // 🛑 Clamp textX and textY to ensure the box stays within canvas bounds
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;

    if (textX + textWidth + padding > canvasWidth) {
      textX = canvasWidth - textWidth - padding;
    }
    if (textY + textHeight + padding > canvasHeight) {
      textY = canvasHeight - textHeight - padding;
    }

    if (textX < padding) textX = padding;
    if (textY < padding) textY = padding;

    if (index !== undefined) {
      textRectsRef.current[index] = {
        x: textX - 4,
        y: textY - 4,
        w: textWidth + 8,
        h: textHeight + 8,
      };
    }

    lines.forEach((line, idx) => {
      ctx.fillText(line, textX, textY + idx * lineHeight);
    });
  };

  const draw = (ctx: CanvasRenderingContext2D) => {
    ctx.clearRect(0, 0, imgDims.width, imgDims.height);
    ctx.lineWidth = 2;
    ctx.strokeStyle = color;
    ctx.fillStyle = color;

    // Draw the image first
    if (imageRef.current) {
      ctx.drawImage(imageRef.current, 0, 0, imgDims.width, imgDims.height);
    }

    textRectsRef.current = [];

    // Draw existing annotations on top
    annotations.forEach(({ start, end, text }, i) => {
      // Convert normalized coordinates (0-1000) to canvas coordinates
      const xScale = imgDims.width / 1000;
      const yScale = imgDims.height / 1000;
      const scaledStart = { x: start.x * xScale, y: start.y * yScale };
      const scaledEnd = { x: end.x * xScale, y: end.y * yScale };
      drawArrow(ctx, scaledStart, scaledEnd, text, i);
    });

    // Draw temporary points/arrow
    if (tempPoints.start && !tempPoints.end) {
      // Convert normalized coordinates to canvas coordinates
      const xScale = imgDims.width / 1000;
      const yScale = imgDims.height / 1000;
      const scaledX = tempPoints.start.x * xScale;
      const scaledY = tempPoints.start.y * yScale;

      ctx.beginPath();
      ctx.arc(scaledX, scaledY, 5, 0, 2 * Math.PI);
      ctx.fill();
    } else if (tempPoints.start && tempPoints.end) {
      // Convert normalized coordinates to canvas coordinates
      const xScale = imgDims.width / 1000;
      const yScale = imgDims.height / 1000;
      const scaledStart = {
        x: tempPoints.start.x * xScale,
        y: tempPoints.start.y * yScale,
      };
      const scaledEnd = {
        x: tempPoints.end.x * xScale,
        y: tempPoints.end.y * yScale,
      };
      drawArrow(ctx, scaledStart, scaledEnd, "");
    }
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (canvas && imgDims.width > 0) {
      const ctx = canvas.getContext("2d");
      if (ctx) draw(ctx);
    }
  }, [annotations, tempPoints, imgDims, imageRef, imageScale, color]);

  return (
    <div className="relative w-full max-w-full mx-auto min-h-[55rem]">
      <img
        ref={imageRef}
        src={imageSrc}
        crossOrigin="anonymous"
        onClick={handleClick}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        draggable={false}
        onLoad={(e) => {
          const img = e.currentTarget;
          setImgDims({
            width: img.naturalWidth,
            height: img.naturalHeight,
          });
        }}
        style={{
          display: "block",
          maxWidth: "100%",
          transform: `scale(${imageScale})`,
          transformOrigin: "top left",
        }}
        alt="Annotatable"
      />
      <canvas
        ref={canvasRef}
        width={imgDims.width}
        height={imgDims.height}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          pointerEvents: "none",
          transform: `scale(${imageScale})`,
          transformOrigin: "top left",
        }}
      />
    </div>
  );
};

export default AnnotationCanvas;
